import icons from './icons';

// One pin shape for every KOOB map, live or exported (KD-3753). The frame
// stays 36x48 with the tip at the bottom centre, so live maps and the PDF
// overlay (ExportStaticMap anchors at -50%/-100%) place it on the exact spot.
const WIDTH = 36;
const HEIGHT = 48;
const HEAD = { x: 18, y: 18 };
const GLYPH_SIZE = 14;

const PIN_PATH =
  'M18 46c-.6 0-1.2-.3-1.6-.8C12.2 39.9 4 29.6 4 18 4 10.3 10.3 4 18 4s14 6.3 14 14c0 11.6-8.2 21.9-12.4 27.2-.4.5-1 .8-1.6.8z';

const GLYPHS = {
  flag: 'flagCheckered',
  AIR: 'plane',
  LAND: 'car',
  RAIL: 'train',
  BOAT: 'ship',
  Activity: 'personHiking',
  start: 'play',
  hotel: 'bed',
  poi: 'camera',
  programDay: 'calendarDay',
  Program: 'route',
};

const isWhite = (color) => /^#f{3,8}$/i.test(color);

function glyph(name, color) {
  const icon = icons[name];
  const scale = GLYPH_SIZE / Math.max(icon.width, icon.height);
  const x = HEAD.x - (icon.width * scale) / 2;
  const y = HEAD.y - (icon.height * scale) / 2;

  return `<path transform="translate(${x} ${y}) scale(${scale})" fill="${color}" d="${icon.path}"/>`;
}

function text(label, color) {
  const size = String(label).length > 2 ? 10 : 13;

  return `<text x="${HEAD.x}" y="${HEAD.y}" dy=".35em" font-family="Inter, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif" font-size="${size}" font-weight="700" fill="${color}" text-anchor="middle">${label}</text>`;
}

function svgPin({ body, ink, content }) {
  // A white pin needs a hairline to stand out from light map areas; a coloured
  // one gets a white ring to stand out from everything else.
  const ring = isWhite(body)
    ? 'stroke="#1B2C49" stroke-opacity=".18" stroke-width="1"'
    : 'stroke="#FFFFFF" stroke-width="2"';

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${WIDTH}" height="${HEIGHT}" viewBox="0 0 ${WIDTH} ${HEIGHT}">
  <defs><filter id="s" x="-40%" y="-20%" width="180%" height="150%"><feDropShadow dx="0" dy="1.5" stdDeviation="1.5" flood-color="#0B1220" flood-opacity=".28"/></filter></defs>
  <path d="${PIN_PATH}" fill="${body}" ${ring} filter="url(#s)"/>
  ${content(ink)}
</svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// One colour per kind of place, the same on every map (KD-3753).
const PIN_COLORS = {
  hotel: '#F5700A',
  Program: '#2563EB',
  Activity: '#16A34A',
  AIR: '#9333EA',
  LAND: '#9333EA',
  RAIL: '#9333EA',
  BOAT: '#9333EA',
  start: '#1B2C49',
  flag: '#1B2C49',
  programDay: '#9C2276',
  poi: '#D7519C',
  day: '#D7519C',
};

// Pins the agent cannot pick (an experience incompatible with the trip).
export const MUTED_PIN_COLOR = '#9CA3AF';

/**
 * Returns a KOOB pin as an SVG data URI. Every kind follows one rule: white
 * with a glyph in its colour, filled with its colour when open or already on
 * the trip. `label` is the number on `day` pins; `color` replaces the kind's
 * colour (the PDF export uses the agency's own). Unknown kinds (a new
 * experience type) draw as a Program.
 */
export const koobMarker = (kind, { filled = false, label, color } = {}) => {
  const type = PIN_COLORS[kind] ? kind : 'Program';
  const tint = color ?? PIN_COLORS[type];
  const body = filled ? tint : '#FFFFFF';
  const ink = filled ? '#FFFFFF' : tint;

  return svgPin({
    body,
    ink,
    content:
      type === 'day' ? (c) => text(label, c) : (c) => glyph(GLYPHS[type], c),
  });
};
